import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faPhone,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";

import Loading from "../../layout/Loading";
import ConfirmDelete from "../../modals/ConfirmDelete";
import Avatar from "../../core/Avatar";
import FormFieldTags from "../../input/FormFieldTags";
import EditAvatar from "../../forms/EditAvatar";
import ChangePasswordModal from "../../forms/ChangePasswordModal";
import Modal from "../../modals/Modal";

import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import useDelayedLoading from "../../../hooks/useDelayedLoading";
import SecurityWrapper from "../../auth/SecurityWrapper";
import { useAuthInfo } from "../../../context/AuthContext";

import {
  checkEditAccess,
  canChangeUserPassword,
} from "../../../helpers/checkAccess";
import { formatPhone } from "../../../util/stringUtils";
import { setCachedBreadcrumbLabel } from "../../../helpers/breadcrumbCache";

const changePasswordModalContent = {
  top: "15%",
  left: "50%",
  transform: "translateX(-50%)",
  width: "420px",
  height: "auto",
  maxHeight: "85vh",
  overflow: "auto",
};

const UserPage = (props) => {
  const { data: user, fetchData, isLoading } = useTriggeredAPICall();
  const { fetchData: updateUserActivation } = useTriggeredAPICall();
  const { userInfo, updateUserInfo } = useAuthInfo();

  const showLoading = useDelayedLoading(isLoading);

  const userId = props.match.params.user_id;

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);

  const handleActivation = async () => {
    if (!user?.results?.user_id) return;

    await updateUserActivation(`/user/${user.results.user_id}`, "PATCH");
    fetchData(`/user/${userId}`);
  };

  const onAvatarSuccess = (data) => {
    if (!data?.results) return;

    fetchData(`/user/${userId}`);

    if (userId === userInfo?.user_id) {
      updateUserInfo((previous) => ({
        ...previous,
        profile_avatar: data.results,
      }));
    }
  };

  useEffect(() => {
    fetchData(`/user/${userId}`, "GET");
  }, [userId, fetchData]);

  useEffect(() => {
    if (user?.results?.user_id) {
      const label = [user.results.first_name, user.results.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (label) {
        setCachedBreadcrumbLabel("user", user.results.user_id, label);
      }
    }
  }, [user]);

  if ((showLoading || isLoading) && !user) {
    return <Loading />;
  }

  if (!user?.results || user.results.user_id !== userId) {
    return showLoading ? <Loading /> : null;
  }

  const isSelf = userInfo?.user_id === user.results.user_id;
  const isSuperAdmin = userInfo.role?.name === "super-admin";
  const canChangePassword = canChangeUserPassword(userInfo, user.results);

  return (
    <div className="width-full">
      <div className="top-section card flex-space-between">
        <button onClick={() => props.history.goBack()}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Back
        </button>
        {userInfo.role?.name !== "user" && (
          <div className="flex-start active-switch-div">
            Active:
            <label className="switch">
              <input
                type="checkbox"
                disabled={!checkEditAccess(userInfo, user.results) || isSelf}
                checked={user.results.is_active}
                onChange={handleActivation}
              />

              <span className="slider round">
                <span>On</span>
                <span>Off</span>
              </span>
            </label>
          </div>
        )}
      </div>
      <div className=" card flex-rows flex-align-center flex-rows-space-around py-20 ">
        <div className="flex-columns flex-align-center ">
          {user.results.first_name && (
            <Avatar
              className="user-avatar"
              userData={user.results}
              onClick={isSelf ? () => setIsAvatarModalOpen(true) : undefined}
            />
          )}
          <h1>
            {user.results.first_name} {user.results.last_name}
          </h1>
        </div>
        <div className="middle-section">
          <div className="icon-and-details">
            <div className="flex-columns width-full info-wrapper ">
              <div>
                <span className="org-label">Organization:</span>

                <Link
                  className="value no-decoration"
                  to={`/organization/${user.results.org_id}`}
                >
                  {user.results.organization.name}
                </Link>
              </div>
              <div>
                <span className="role-label">Role:</span>
                <span className="value">{user.results.role?.name}</span>
              </div>
              <div>
                <span className="email-label">
                  <FontAwesomeIcon icon={faEnvelope} />
                  Email:
                </span>
                <span className="value">{user.results.email}</span>
              </div>
              <div>
                <span className="phone-label">
                  <FontAwesomeIcon icon={faPhone} />
                  Phone:
                </span>
                <span className="value">{formatPhone(user.results.phone)}</span>
              </div>
              <div className="flex-rows width-full">
                <span className="tag-label">Tags:</span>
                <FormFieldTags selectedTags={user.results.tags} />
              </div>
              <div className="flex-rows gap-10">
                {checkEditAccess(userInfo, user.results) && (
                  <button
                    onClick={() =>
                      props.history.push(`/user-add/${user.results.user_id}`)
                    }
                  >
                    Edit
                  </button>
                )}
                {canChangePassword && (
                  <button onClick={() => setIsChangePasswordModalOpen(true)}>
                    Change Password
                  </button>
                )}
                {isSuperAdmin && !isSelf && (
                  <ConfirmDelete objectType="user" id={user.results.user_id} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isModalOpen={isAvatarModalOpen}
        onRequestClose={() => setIsAvatarModalOpen(false)}
      >
        <EditAvatar
          onUpdate={onAvatarSuccess}
          onClose={() => setIsAvatarModalOpen(false)}
        />
      </Modal>

      <Modal
        isModalOpen={isChangePasswordModalOpen}
        onRequestClose={() => setIsChangePasswordModalOpen(false)}
        content={changePasswordModalContent}
      >
        <ChangePasswordModal
          userId={user.results.user_id}
          requireCurrentPassword={isSelf}
          onClose={() => setIsChangePasswordModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default UserPage;
